import MapboxDraw from '@mapbox/mapbox-gl-draw';

const { CommonSelectors, createSupplementaryPoints } = MapboxDraw.lib;
const { cursors, meta } = MapboxDraw.constants;

const isVertex = CommonSelectors.isVertex;
const isMidpoint = CommonSelectors.isOfMetaType(meta.MIDPOINT);
const isActiveFeature = CommonSelectors.isActiveFeature;

/**
 * Prefer handles slightly beyond draw-options clickBuffer (10) so the edge stroke
 * does not swallow midpoints.
 */
const HANDLE_SNAP_PX = 14;

type MapPoint = { x: number; y: number };

type DrawFeatureTarget = {
  type?: string;
  properties?: {
    meta?: string;
    id?: string;
    parent?: string;
    coord_path?: string;
    lng?: number;
    lat?: number;
    active?: string;
  };
  geometry?: { type?: string; coordinates?: number[] };
};

type DrawPointerEvent = {
  point?: MapPoint;
  featureTarget?: DrawFeatureTarget;
};

type DrawFeature = {
  id: string;
  type: string;
  toGeoJSON: () => {
    id?: string;
    type: string;
    properties?: Record<string, unknown>;
    geometry: { type: string; coordinates: unknown };
  };
};

type DrawModeContext = {
  map: {
    project: (lngLat: [number, number]) => MapPoint;
  };
  featuresAt: (
    event: DrawPointerEvent,
    bbox: null,
    bufferType: 'click' | 'touch'
  ) => DrawFeatureTarget[];
  updateUIClasses: (opts: { mouse: string }) => void;
  onVertex: (state: object, e: DrawPointerEvent) => void;
  onMidpoint: (state: object, e: DrawPointerEvent) => void;
  onFeature: (state: object, e: DrawPointerEvent) => void;
  fireUpdate: () => void;
  stopDragging: (state: object) => void;
};

function isHandle(feature?: DrawFeatureTarget): boolean {
  const handleMeta = feature?.properties?.meta;
  return handleMeta === meta.VERTEX || handleMeta === meta.MIDPOINT;
}

function distancePx(a: MapPoint, b: MapPoint): number {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Mapbox Draw hit-testing often returns the polygon/line (boundary) under an edge
 * midpoint because the stroke layer sits above the midpoint circle. When that
 * happens, featureTarget is meta=feature → pointer cursor and our no-op onFeature.
 * Prefer any vertex/midpoint from the query, then snap geometrically to nearby handles.
 */
function preferEditableHandle(
  ctx: DrawModeContext,
  e: DrawPointerEvent,
  feature: DrawFeature | undefined,
  bufferType: 'click' | 'touch'
): void {
  const hits = ctx.featuresAt(e, null, bufferType) ?? [];
  const queriedHandle = hits.find(isHandle);
  if (queriedHandle) {
    e.featureTarget = queriedHandle;
    return;
  }

  if (!feature || !e.point) {
    return;
  }

  const geojson = feature.toGeoJSON();
  geojson.properties = {
    ...geojson.properties,
    id: feature.id ?? geojson.id,
  };

  const handles = createSupplementaryPoints(geojson, { midpoints: true }) as DrawFeatureTarget[];

  let best: DrawFeatureTarget | undefined;
  let bestDist = HANDLE_SNAP_PX;

  for (const handle of handles) {
    const coordinates = handle.geometry?.coordinates;
    if (!coordinates || coordinates.length < 2) {
      continue;
    }

    const projected = ctx.map.project([coordinates[0], coordinates[1]]);
    const dist = distancePx(projected, e.point);
    if (dist > bestDist) {
      continue;
    }

    // At equal distance, keep a vertex over a midpoint.
    if (
      best &&
      best.properties?.meta === meta.VERTEX &&
      handle.properties?.meta === meta.MIDPOINT &&
      dist === bestDist
    ) {
      continue;
    }

    best = handle;
    bestDist = dist;
  }

  if (best) {
    e.featureTarget = best;
  }
}

function cursorForDrawTarget(e: DrawPointerEvent): string {
  if (isVertex(e) || isMidpoint(e)) {
    return cursors.MOVE;
  }
  // Boundary/feature clicks are intentionally inert — do not show a pointer.
  return cursors.NONE;
}

function handleDirectSelectPointerDown(
  this: DrawModeContext,
  state: { feature: DrawFeature },
  e: DrawPointerEvent,
  bufferType: 'click' | 'touch'
) {
  preferEditableHandle(this, e, state.feature, bufferType);
  if (isVertex(e)) {
    return this.onVertex(state, e);
  }
  if (isMidpoint(e)) {
    return this.onMidpoint(state, e);
  }
  if (isActiveFeature(e)) {
    return this.onFeature(state, e);
  }
}

/**
 * Mapbox Draw modes with whole-feature drag disabled.
 * Vertices and edge midpoints remain draggable in direct_select.
 */
const simpleSelect = {
  ...MapboxDraw.modes.simple_select,
  startOnActiveFeature(state: object) {
    this.stopExtendedInteractions(state);
  },
  onMouseMove(state: object, e: DrawPointerEvent) {
    MapboxDraw.modes.simple_select.onMouseMove.call(this, state, e);
    this.updateUIClasses({ mouse: cursorForDrawTarget(e) });
    return true;
  },
};

const directSelect = {
  ...MapboxDraw.modes.direct_select,
  onFeature() {
    // Do not start dragging the entire shape.
  },
  dragFeature() {
    // No-op when drag would move all coordinates at once.
  },
  onMouseDown(
    state: { feature: DrawFeature; selectedCoordPaths: string[] },
    e: DrawPointerEvent
  ) {
    return handleDirectSelectPointerDown.call(this, state, e, 'click');
  },
  onTouchStart(
    state: { feature: DrawFeature; selectedCoordPaths: string[] },
    e: DrawPointerEvent
  ) {
    return handleDirectSelectPointerDown.call(this, state, e, 'touch');
  },
  onMouseMove(
    state: {
      feature: DrawFeature;
      selectedCoordPaths: string[];
      dragMoving?: boolean;
    },
    e: DrawPointerEvent
  ) {
    preferEditableHandle(this, e, state.feature, 'click');
    this.updateUIClasses({ mouse: cursorForDrawTarget(e) });
    if ((isVertex(e) || isMidpoint(e)) && state.dragMoving) {
      this.fireUpdate();
    }
    this.stopDragging(state);
    return true;
  },
};

export const MAPBOX_DRAW_MODES = {
  ...MapboxDraw.modes,
  simple_select: simpleSelect,
  direct_select: directSelect,
};
